/**
 * reCAPTCHA v3 on the front office, rendered explicitly.
 *
 * The Blade layout loads api.js with `render=explicit`, which suppresses the widget Google would
 * otherwise create for us. We then render our own with `size: 'invisible'` — the parameter that
 * gives control over the badge — and execute against the widget id render() hands back.
 *
 * The badge itself is hidden in CSS, which Google permits only while the reCAPTCHA branding stays
 * visible in the flow; RegistrationForm carries that notice under the submit button.
 *
 * Every failure resolves to null rather than throwing: a captcha that cannot run must not be what
 * stops someone from registering. The server decides what to do with a missing token.
 */

interface RenderParameters {
  sitekey: string;
  size: 'invisible';
  badge: 'bottomright' | 'bottomleft' | 'inline';
}

interface Grecaptcha {
  ready: (callback: () => void) => void;
  render: (container: HTMLElement, parameters: RenderParameters) => number;
  execute: (widgetId: number, options: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

/** How long we wait for the Google script to finish loading before giving up. */
const READY_TIMEOUT_MS = 5000;

export function useRecaptcha(siteKey?: string | null) {
  let widgetId: number | null = null;

  const isEnabled = (): boolean => !!siteKey;

  const waitForGrecaptcha = (): Promise<Grecaptcha | null> =>
    new Promise((resolve) => {
      const started = Date.now();

      const poll = () => {
        if (window.grecaptcha) {
          // ready() waits for Google to finish its own initialisation before render() is callable.
          window.grecaptcha.ready(() => resolve(window.grecaptcha ?? null));

          return;
        }

        if (Date.now() - started >= READY_TIMEOUT_MS) {
          resolve(null);

          return;
        }

        setTimeout(poll, 100);
      };

      poll();
    });

  /**
   * An invisible widget, created once. It needs an element to live in, and since the badge is
   * hidden anyway that element is ours rather than something a template has to provide.
   */
  const widget = async (grecaptcha: Grecaptcha): Promise<number | null> => {
    if (widgetId !== null) {
      return widgetId;
    }

    const container = document.createElement('div');
    document.body.appendChild(container);

    try {
      // Rendering twice into one element throws, hence the memo above.
      widgetId = grecaptcha.render(container, {
        sitekey: siteKey as string,
        size: 'invisible',
        badge: 'bottomright',
      });
    } catch (error) {
      console.error('reCAPTCHA could not be rendered', error);
      container.remove();

      return null;
    }

    return widgetId;
  };

  /**
   * Resolves a one-shot token for the given action, or null when reCAPTCHA is off, never loaded,
   * or refused to run.
   */
  const executeRecaptcha = async (action: string): Promise<string | null> => {
    if (!siteKey) {
      return null;
    }

    const grecaptcha = await waitForGrecaptcha();

    if (!grecaptcha) {
      return null;
    }

    const id = await widget(grecaptcha);

    if (id === null) {
      return null;
    }

    try {
      return await grecaptcha.execute(id, { action });
    } catch (error) {
      console.error('reCAPTCHA execution failed', error);

      return null;
    }
  };

  return {
    isEnabled,
    executeRecaptcha,
  };
}

export default useRecaptcha;
